import { create } from 'zustand';
import { fetchConversations } from '@/api/chat';
import type { ChatConversation, ChatMessage } from '@/types/chat';

interface ChatState {
  conversations: ChatConversation[];
  // messages keyed by conversationId
  messagesByConv: Record<string, ChatMessage[]>;
  totalUnread: number;

  setConversations: (convs: ChatConversation[]) => void;
  upsertConversation: (conv: ChatConversation) => void;
  updateConversationStatus: (conversationId: string, status: 'active' | 'archived') => void;
  updateConversationUnread: (conversationId: string, unreadCount: number) => void;
  setMessages: (conversationId: string, messages: ChatMessage[]) => void;
  appendMessage: (message: ChatMessage) => void;
  setTotalUnread: (n: number) => void;
  incrementUnread: (conversationId: string) => void;
  /**
   * Resuelve la conversación de un agente, hydrating el store si hace falta.
   *
   * `conversations` sólo se poblaba desde la página /chat, así que abrir el
   * chat directo sobre la vista del agente (o entrar a /chat/:agentId por
   * URL) dejaba el panel en blanco: sin conversationId no hay fetch de
   * mensajes. Idempotente y sin throw — un fallo de red sólo implica
   * "sin historial todavía".
   */
  ensureConversation: (agentId: string) => Promise<ChatConversation | null>;
}

/** Mínimo necesario para que una conversación recién creada exista en el store. */
function conversationFromMessage(message: ChatMessage): ChatConversation {
  return {
    conversationId: message.conversationId,
    agentId: message.agentId,
    agentHostname: message.agentId,
    status: 'active',
    createdAt: message.createdAt,
    lastMessageAt: message.createdAt,
    unreadCount: 0,
  };
}

export const useChatStore = create<ChatState>((set, get) => ({
  conversations: [],
  messagesByConv: {},
  totalUnread: 0,

  ensureConversation: async (agentId) => {
    const cached = get().conversations.find((c) => c.agentId === agentId);
    if (cached) return cached;
    try {
      const convs = await fetchConversations();
      const match = convs.find((c) => c.agentId === agentId);
      if (match) get().upsertConversation(match);
      return match ?? null;
    } catch {
      return null;
    }
  },

  setConversations: (convs) =>
    set({
      conversations: convs,
      totalUnread: convs
        .filter((c) => c.status === 'active')
        .reduce((s, c) => s + c.unreadCount, 0),
    }),

  upsertConversation: (conv) =>
    set((state) => {
      const idx = state.conversations.findIndex((c) => c.conversationId === conv.conversationId);
      let convs: ChatConversation[];
      if (idx >= 0) {
        convs = [...state.conversations];
        convs[idx] = conv;
      } else {
        convs = [conv, ...state.conversations];
      }
      const totalUnread = convs
        .filter((c) => c.status === 'active')
        .reduce((s, c) => s + c.unreadCount, 0);
      return { conversations: convs, totalUnread };
    }),

  updateConversationStatus: (conversationId, status) =>
    set((state) => {
      const convs = state.conversations.map((c) =>
        c.conversationId === conversationId ? { ...c, status } : c
      );
      const totalUnread = convs
        .filter((c) => c.status === 'active')
        .reduce((s, c) => s + c.unreadCount, 0);
      return { conversations: convs, totalUnread };
    }),

  updateConversationUnread: (conversationId, unreadCount) =>
    set((state) => {
      const convs = state.conversations.map((c) =>
        c.conversationId === conversationId ? { ...c, unreadCount } : c
      );
      const totalUnread = convs
        .filter((c) => c.status === 'active')
        .reduce((s, c) => s + c.unreadCount, 0);
      return { conversations: convs, totalUnread };
    }),

  setMessages: (conversationId, messages) =>
    set((state) => ({
      messagesByConv: { ...state.messagesByConv, [conversationId]: messages },
    })),

  appendMessage: (message) =>
    set((state) => {
      const existing = state.messagesByConv[message.conversationId] ?? [];
      // Deduplicate by messageId
      if (existing.some((m) => m.messageId === message.messageId)) {
        return state;
      }
      const updated = [...existing, message];
      // Si la conversación todavía no está en el store (se acaba de crear al
      // mandar el primer mensaje desde el chat flotante), la registramos para
      // que el panel siga encontrando el conversationId al reabrirse.
      const known = state.conversations.some((c) => c.conversationId === message.conversationId);
      const base = known
        ? state.conversations
        : [conversationFromMessage(message), ...state.conversations];
      // Update conversation lastMessage and lastMessageAt
      const convs = base.map((c) => {
        if (c.conversationId !== message.conversationId) return c;
        return {
          ...c,
          lastMessage: message,
          lastMessageAt: message.createdAt,
          unreadCount: message.senderType === 'agent' ? c.unreadCount + 1 : c.unreadCount,
        };
      });
      const totalUnread = convs
        .filter((c) => c.status === 'active')
        .reduce((s, c) => s + c.unreadCount, 0);
      return {
        messagesByConv: { ...state.messagesByConv, [message.conversationId]: updated },
        conversations: convs,
        totalUnread,
      };
    }),

  setTotalUnread: (n) => set({ totalUnread: n }),

  incrementUnread: (conversationId) =>
    set((state) => {
      const convs = state.conversations.map((c) =>
        c.conversationId === conversationId ? { ...c, unreadCount: c.unreadCount + 1 } : c
      );
      const totalUnread = convs
        .filter((c) => c.status === 'active')
        .reduce((s, c) => s + c.unreadCount, 0);
      return { conversations: convs, totalUnread };
    }),
}));
